import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Form, Input, DatePicker, InputNumber, message } from 'antd';
import { useNavigate, useParams } from 'react-router';
import { ENavigationPath } from 'constants/path';
import {
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  CreateReceiptPayloadDto,
  CreateReceiptPayloadDtoReceiptTypeEnum,
  UpdateReceiptPayloadDto,
  BrokerResponseDtoStatusEnum,
  PremiumScheduleAllocationDto,
  PremiumScheduleAvailableForAllocationResponseDto,
  SyncReceiptSettlementExchangeRatePayloadDto,
} from 'libs/pinnacle-sdk';
import { ConfirmationModal } from 'components/ui/confirmation-modal';
import { debounce, isEmpty, isEqual, isNil } from 'lodash';
import { handleError, sanitizeFormValue, validateForm } from 'utils/form';
import { fields, getValidationSchema, initialValues } from './form';
import { ConfirmationModalType } from 'components/ui/confirmation-modal/types';
import FormContainer from 'components/ui/form-container';
import FormSection from 'components/ui/form-section';
import FormLayoutGroup from 'components/ui/form-layout-group';
import FormActionButtons from 'components/ui/form-action-buttons';
import { ArrowDownIcon } from 'assets/icons/arrow-down';
import FileUploader from 'components/features/file-uploader';
import { useTranslation } from 'react-i18next';
import CalendarIcon from 'assets/icons/calendar';
import { EFileStatus } from '../file-uploader/enum';
import {
  brokerApi,
  premiumScheduleApi,
  receiptApi,
  referenceDataApi,
} from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import TextArea from 'antd/es/input/TextArea';
import { formatLocaleNumber, formatReceiptNumber } from 'utils/format';
import { useFileSession } from 'hooks/useFileSession';
import dayjs from 'dayjs';
import { LazyLoadingSelect } from 'components/ui/lazy-loading-select';
import ContractAllocationTable from 'components/features/contract-allocation-table';
import ContractUnAllocationTable from 'components/features/contract-unallocation-table';
import { useWatch } from 'antd/es/form/Form';
import { ContractAllocationRow } from 'components/features/contract-allocation-table/types';
import { SortedSelect } from 'components/ui/sorted-select';
import SettlementExchangeRateTable, {
  SettlementExchangeRateItem,
} from 'components/features/settlement-exchange-rate-table';
import { uniqAndFilterNil } from 'utils';
import { FIXED_SETTLEMENT_CURRENCIES } from 'components/features/contract-allocation-table/constants';

interface ReceiptFormProps {
  mode: 'create' | 'update';
  onSubmit?: (data: {
    receipt: CreateReceiptPayloadDto;
    fileIds: string[];
    premiumScheduleAllocations?: PremiumScheduleAllocationDto[];
  }) => Promise<void>;
  isLoading?: boolean;
}

export const ReceiptForm: React.FC<ReceiptFormProps> = ({
  mode,
  isLoading = false,
  onSubmit,
}) => {
  const [isAllocationDataValid, setIsAllocationDataValid] = useState(true);
  const [isFormValid, setIsFormValid] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { t } = useTranslation();
  const hasFormBeenUpdated = useRef(false);
  const isCreateMode = useMemo(() => mode === 'create', [mode]);
  const { id } = useParams<{ id?: string }>();
  const queryClient = useQueryClient();
  const [allocationContractTableData, setAllocationContractTableData] =
    useState<ContractAllocationRow[]>([]);
  const [contractsAllocated, setContractsAllocated] = useState<
    ContractAllocationRow[]
  >([]);
  const [receiptSettlementExchangeRates, setReceiptSettlementExchangeRates] =
    useState<SettlementExchangeRateItem[]>([]);
  const [isSettlementRateValid, setIsSettlementRateValid] = useState(true);
  const amountWatch = useWatch<number>('amount', form);
  const bankFeeWatch = useWatch<number>('bankFee', form);

  const remainingAmount = useMemo(() => {
    if (isNil(amountWatch)) {
      return 0;
    }

    const bankFee = bankFeeWatch ?? 0;
    const netAmount = amountWatch - bankFee;

    if (isEmpty(contractsAllocated)) {
      return Number(netAmount.toFixed(2));
    }

    const totalAllocatedAmount = contractsAllocated.reduce(
      (acc, contract) => acc + (contract.paidAmount || 0),
      0
    );

    return Number((netAmount - totalAllocatedAmount).toFixed(2));
  }, [amountWatch, bankFeeWatch, contractsAllocated]);

  const resetAndNavigate = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    navigate(ENavigationPath.LIST_RECEIPTS);
  }, [form, navigate]);

  const {
    fileSessionId,
    setFileList,
    fileList,
    setFileListWithInitialFileList,
  } = useFileSession();

  const sourceBrokerId = useWatch<string>('sourceBrokerId', form);
  const bankAccountCurrency = useWatch<string>('currency', form);

  const { data: bankAccounts, isLoading: isLoadingBankAccounts } = useQuery(
    EQueryKeys.LIST_BANK_ACCOUNTS,
    async () => {
      return referenceDataApi.getBankAccounts();
    },
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const { data: receipt } = useQuery(
    [EQueryKeys.RECEIPT_BY_ID, id],
    () => receiptApi.getReceiptById({ receiptId: Number(id) }),
    {
      enabled: !!id && mode === 'update',
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }
  );

  useEffect(() => {
    setReceiptSettlementExchangeRates(
      (receipt?.settlementExchangeRates ?? []).map((rate, index) => ({
        id: index + 1,
        originalCurrency: rate.originalCurrency,
        settlementCurrency: bankAccountCurrency,
        settlementExchangeRate: Number(rate.settlementExchangeRate),
      }))
    );
  }, [receipt?.settlementExchangeRates, bankAccountCurrency]);

  const updateReceiptMutation = useMutation<
    void,
    { response: Response },
    {
      id: number;
      receipt: UpdateReceiptPayloadDto;
      fileIds: string[];
      receiptSettlementExchangeRates?: SyncReceiptSettlementExchangeRatePayloadDto[];
    }
  >(
    async ({ id, receipt, fileIds, receiptSettlementExchangeRates }) => {
      await receiptApi.updateReceiptById({
        receiptId: id,
        updateReceiptPayloadDto: {
          ...receipt,
          fileIds,
          premiumScheduleAllocations: contractsAllocated.map((contract) => ({
            premiumScheduleId: contract.id,
            settlementExchangeRate: String(
              contract.settlementExchangeRate || 1
            ),
          })),
          receiptSettlementExchangeRates,
        },
      });
    },
    {
      onSuccess: () => {
        message.success(
          t('forms.updateRecordSuccess', {
            type: t('cashReceipts.receipt'),
          })
        );
        queryClient
          .invalidateQueries([EQueryKeys.RECEIPT_BY_ID, id])
          .then(() => {
            navigate(ENavigationPath.LIST_RECEIPTS);
          });
      },
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('forms.updateRecordFail', {
            type: t('cashReceipts.receipt'),
          }),
        });
      },
    }
  );

  useEffect(() => {
    const initializeForm = async () => {
      if (receipt) {
        const initialData = {
          ...receipt,
          transactionDate: receipt.transactionDate
            ? dayjs(receipt.transactionDate)
            : undefined,
          currency: receipt.bankAccount.currencyCode,
          receiptNumber: formatReceiptNumber(receipt.id),
        };
        form.setFieldsValue(initialData);

        const isValid = await validateForm(
          initialData,
          form,
          fields,
          getValidationSchema(t)
        );
        setIsFormValid(isValid);
        hasFormBeenUpdated.current = true;

        setFileListWithInitialFileList(receipt.files || []);
      }
    };

    initializeForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receipt, setFileListWithInitialFileList]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpsertReceiptMutate = useCallback(
    debounce(
      (data: {
        receipt: CreateReceiptPayloadDto;
        fileIds: string[];
        premiumScheduleAllocations?: PremiumScheduleAllocationDto[];
        receiptSettlementExchangeRates?: SyncReceiptSettlementExchangeRatePayloadDto[];
      }) => {
        if (mode === 'create' && onSubmit) {
          onSubmit(data)
            .then(() => {
              message.success(
                t(
                  isCreateMode
                    ? 'forms.createRecordSuccess'
                    : 'forms.updateRecordSuccess',
                  {
                    type: t('cashReceipts.receipt'),
                  }
                )
              );
              navigate(ENavigationPath.LIST_RECEIPTS);
            })
            .catch((error) => {
              handleError({
                error,
                defaultMessage: t('forms.updateRecordFail', {
                  type: t('cashReceipts.receipt'),
                }),
              });
            });
        } else if (mode === 'update') {
          updateReceiptMutation.mutate({
            id: Number(id),
            receipt: data.receipt,
            fileIds: data.fileIds,
            receiptSettlementExchangeRates: data.receiptSettlementExchangeRates,
          });
        }
      },
      500
    ),
    [mode, onSubmit, navigate, t]
  );

  const { data: contractAllocatedToReceipt } = useQuery(
    [EQueryKeys.CONTRACT_ALLOCATED_TO_RECEIPT, id],
    () => {
      return premiumScheduleApi.getAllocatedPremiumSchedule({
        getAllocatedPremiumScheduleQueryDto: {
          receiptId: Number(id),
        },
      });
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    setContractsAllocated(
      contractAllocatedToReceipt?.premiumSchedules.map((premiumSchedule) => ({
        id: premiumSchedule.premiumScheduleId,
        isSelected: false,
        documentNo: premiumSchedule.contractPinnacleReferenceNumber || '',
        insured: premiumSchedule.insuredName,
        reinsured: premiumSchedule.reinsuredName,
        binder: premiumSchedule.binderName,
        originalNetAmountDueBroker: premiumSchedule.originalNetAmountDueBroker
          ? Number(premiumSchedule.originalNetAmountDueBroker)
          : 0,
        originalCurrency: premiumSchedule.submissionOriginalCurrency,
        bookedExchangeRate: premiumSchedule.bookedExchangeRate
          ? Number(premiumSchedule.bookedExchangeRate)
          : undefined,
        shareOfPremiumSigned: premiumSchedule.shareOfPremiumSigned
          ? Number(premiumSchedule.shareOfPremiumSigned)
          : 0,
        settlementExchangeRate: premiumSchedule.settlementExchangeRate
          ? Number(premiumSchedule.settlementExchangeRate)
          : undefined,
        paidAmount: premiumSchedule.paidAmount
          ? Number(premiumSchedule.paidAmount)
          : undefined,
        ppwDueDate: premiumSchedule.ppwDueDate,
        closing: premiumSchedule.closing,
      })) || []
    );
  }, [contractAllocatedToReceipt]);

  const allocationContractTableDataCurrencies = useMemo(() => {
    return uniqAndFilterNil(
      allocationContractTableData.map((row) => row.originalCurrency)
    );
  }, [allocationContractTableData]);

  return (
    <FormContainer
      title={t(
        mode === 'create'
          ? 'cashReceipts.receipting'
          : 'cashReceipts.updateReceipt'
      )}
      loading={isLoading}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        className="space-y-8"
        onValuesChange={async () => {
          const values = form.getFieldsValue();
          const isValid = await validateForm(
            values,
            form,
            fields,
            getValidationSchema(t)
          );
          setIsFormValid(isValid);
        }}
        onFinish={async (receipt: CreateReceiptPayloadDto) => {
          const sanitizedReceipt = sanitizeFormValue(receipt);
          const fileIds = fileList
            .filter((file) => !!file.id)
            .map((file) => file.id) as string[];
          debouncedUpsertReceiptMutate({
            receipt: sanitizedReceipt,
            fileIds,
            premiumScheduleAllocations: contractsAllocated.map((contract) => ({
              premiumScheduleId: contract.id,
              settlementExchangeRate: String(
                contract.settlementExchangeRate || 1
              ),
            })),
            receiptSettlementExchangeRates: receiptSettlementExchangeRates
              .filter((rate) => !isNil(rate.settlementExchangeRate))
              .map((rate) => ({
                originalCurrency: rate.originalCurrency,
                settlementCurrency: rate.settlementCurrency,
                settlementExchangeRate: String(rate.settlementExchangeRate),
              })),
          });
        }}
      >
        <FormSection title={t('cashReceipts.receiptDetails')} collapsible>
          <FormLayoutGroup mdColumns={isCreateMode ? 2 : 3}>
            {!isCreateMode && (
              <Form.Item
                name="receiptNumber"
                label={t('cashReceipts.receiptNumber')}
              >
                <Input
                  size="large"
                  disabled
                  placeholder={t('general.enter', {
                    field: t('cashReceipts.receiptNumber'),
                  })}
                />
              </Form.Item>
            )}

            <Form.Item
              name="transactionDate"
              label={t('cashReceipts.paymentDate')}
            >
              <DatePicker
                allowClear
                format={'DD/MM/YYYY'}
                size="large"
                className="w-full"
                suffixIcon={<CalendarIcon />}
              />
            </Form.Item>

            <Form.Item name="receiptType" label={t('cashReceipts.receiptType')}>
              <SortedSelect
                allowClear
                size="large"
                placeholder={t('general.selectField', {
                  field: t('cashReceipts.receiptType'),
                })}
                options={Object.values(
                  CreateReceiptPayloadDtoReceiptTypeEnum
                ).map((receiptType) => ({
                  label: receiptType,
                  value: receiptType,
                }))}
                suffixIcon={<ArrowDownIcon />}
              ></SortedSelect>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection title={t('cashReceipts.bankDetails')} collapsible>
          <FormLayoutGroup mdColumns={2}>
            <Form.Item name="bankAccountNumber" label={t('cashReceipts.bank')}>
              <SortedSelect
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('cashReceipts.selectBank')}
                loading={isLoadingBankAccounts}
                options={(bankAccounts || []).map((bankAccount) => ({
                  label: `${bankAccount.accountName} (${bankAccount.currencyCode})`,
                  value: bankAccount.accountNumber,
                }))}
                onChange={(value) => {
                  const bankAccount = bankAccounts?.find(
                    (bankAccount) => bankAccount.accountNumber === value
                  );
                  if (bankAccount) {
                    form.setFieldValue('currency', bankAccount.currencyCode);
                  }
                  form.validateFields(['currency']);
                }}
                disabled={contractsAllocated.length > 0}
              ></SortedSelect>
            </Form.Item>

            <Form.Item name="currency" label={t('cashReceipts.currency')}>
              <Input
                size="large"
                className="w-full"
                disabled
                placeholder={t('cashReceipts.currency')}
              />
            </Form.Item>

            <Form.Item name="amount" label={t('cashReceipts.amount')}>
              <InputNumber<number>
                size="large"
                className="w-full"
                precision={2}
                formatter={formatLocaleNumber}
                placeholder={t('general.enter', {
                  field: t('cashReceipts.amount'),
                })}
              />
            </Form.Item>

            <Form.Item name="bankFee" label={t('cashReceipts.bankFee')}>
              <InputNumber<number>
                size="large"
                className="w-full"
                precision={2}
                formatter={formatLocaleNumber}
                placeholder={t('general.enter', {
                  field: t('cashReceipts.bankFee'),
                })}
              />
            </Form.Item>

            <Form.Item label={t('cashReceipts.remainingAmount')}>
              <InputNumber<number>
                size="large"
                value={remainingAmount}
                className="w-full"
                precision={2}
                formatter={formatLocaleNumber}
                disabled
                placeholder={t('general.enter', {
                  field: t('cashReceipts.remainingAmount'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection title={t('cashReceipts.brokerDetails')} collapsible>
          <FormLayoutGroup>
            <Form.Item
              name="sourceBrokerId"
              label={t('cashReceipts.sourceBrokerPayer')}
            >
              <LazyLoadingSelect
                fetchOptions={async (page, pageSize, searchText) => {
                  const { items } = await brokerApi.filterBrokers({
                    status: BrokerResponseDtoStatusEnum.Active,
                    searchText,
                    page,
                    pageSize,
                  });
                  return {
                    data: items.map((user) => ({
                      label: user.name || '',
                      value: user.brokerId,
                    })),
                  };
                }}
                additionalOptions={
                  receipt?.sourceBroker
                    ? [
                        {
                          label: receipt.sourceBroker.name,
                          value: receipt.sourceBroker.brokerId,
                        },
                      ]
                    : []
                }
                suffixIcon={<ArrowDownIcon />}
                allowClear
                size="large"
                placeholder={t('cashReceipts.selectSourceBrokers')}
                queryKey="source-broker-lazy-loading-select"
                disabled={contractsAllocated.length > 0}
              />
            </Form.Item>

            <Form.Item
              name="sourceBrokerReference"
              label={t('cashReceipts.paymentReference')}
            >
              <Input
                size="large"
                className="w-full"
                placeholder={t('general.enter', {
                  field: t('cashReceipts.paymentReference'),
                })}
              />
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        {sourceBrokerId && bankAccountCurrency && (
          <>
            {!FIXED_SETTLEMENT_CURRENCIES.includes(bankAccountCurrency) && (
              <FormSection
                title={t('contractAllocation.settlementExchangeRate')}
                collapsible
              >
                <FormLayoutGroup mdColumns={1}>
                  <SettlementExchangeRateTable
                    data={receiptSettlementExchangeRates}
                    bankAccountCurrency={bankAccountCurrency}
                    onRateChanged={setReceiptSettlementExchangeRates}
                    onRateValidated={setIsSettlementRateValid}
                    allocationContractTableDataCurrencies={
                      allocationContractTableDataCurrencies
                    }
                    sourceBrokerId={sourceBrokerId}
                  />
                </FormLayoutGroup>
              </FormSection>
            )}
            <FormSection
              title={t('contractAllocation.contractsAvailableForAllocation')}
              collapsible
            >
              <FormLayoutGroup mdColumns={1}>
                <ContractAllocationTable
                  form={form}
                  onContractDataChanged={setAllocationContractTableData}
                  onContractAllocated={(selectedContracts) => {
                    setContractsAllocated((prev) => [
                      ...prev,
                      ...selectedContracts.map((row) => ({
                        ...row,
                        isSelected: false,
                      })),
                    ]);
                    setAllocationContractTableData((prev) =>
                      prev.filter(
                        (row) =>
                          !selectedContracts.some(
                            (selectedContract) => selectedContract.id === row.id
                          )
                      )
                    );
                  }}
                  sourceBrokerId={sourceBrokerId}
                  allocationContractTableData={allocationContractTableData}
                  bankAccountCurrency={bankAccountCurrency}
                  isAllocationDataValid={isAllocationDataValid}
                  onAllocationDataValidated={setIsAllocationDataValid}
                  onAvailableAllocationFetched={(
                    contractAvailableForAllocation: PremiumScheduleAvailableForAllocationResponseDto[]
                  ) =>
                    setAllocationContractTableData(
                      (contractAvailableForAllocation ?? []).map(
                        (premiumSchedule) => ({
                          id: premiumSchedule.premiumScheduleId,
                          isSelected: false,
                          documentNo:
                            premiumSchedule.contractPinnacleReferenceNumber ||
                            '',
                          insured: premiumSchedule.insuredName,
                          reinsured: premiumSchedule.reinsuredName,
                          binder: premiumSchedule.binderName,
                          originalNetAmountDueBroker:
                            premiumSchedule.originalNetAmountDueBroker
                              ? Number(
                                  premiumSchedule.originalNetAmountDueBroker
                                )
                              : 0,
                          originalCurrency:
                            premiumSchedule.submissionOriginalCurrency,
                          bookedExchangeRate: premiumSchedule.bookedExchangeRate
                            ? Number(premiumSchedule.bookedExchangeRate)
                            : undefined,
                          shareOfPremiumSigned:
                            premiumSchedule.shareOfPremiumSigned
                              ? Number(premiumSchedule.shareOfPremiumSigned)
                              : 0,
                          ppwDueDate: premiumSchedule.ppwDueDate,
                          closing: premiumSchedule.closing,
                          closingReference: premiumSchedule.closingReference,
                        })
                      )
                    )
                  }
                  receiptSettlementExchangeRates={
                    receiptSettlementExchangeRates
                  }
                />
              </FormLayoutGroup>
            </FormSection>

            <FormSection
              title={t('contractAllocation.policiesAllocatedAgainstReceipt')}
              collapsible
            >
              <FormLayoutGroup mdColumns={1}>
                <ContractUnAllocationTable
                  data={contractsAllocated}
                  onContractUnallocated={(selectedContracts) => {
                    setAllocationContractTableData((prev) => [
                      ...prev,
                      ...selectedContracts.map((row) => ({
                        ...row,
                        isSelected: false,
                      })),
                    ]);
                    setContractsAllocated(
                      contractsAllocated.filter(
                        (contract) =>
                          !selectedContracts.some(
                            (selectedContract) =>
                              selectedContract.id === contract.id
                          )
                      )
                    );
                  }}
                />
              </FormLayoutGroup>
            </FormSection>
          </>
        )}

        <FormSection title={t('cashReceipts.attachments')} collapsible>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item className="mt-6">
              {fileSessionId && (
                <FileUploader
                  sessionId={fileSessionId}
                  entityType={
                    CreateUploadedFilePayloadDtoEntityTypeEnum.Receipt
                  }
                  fileList={fileList}
                  setFileList={setFileList}
                />
              )}
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormSection title={t('cashReceipts.employeeNotes')} collapsible>
          <FormLayoutGroup mdColumns={1}>
            <Form.Item name="comment" label={t('cashReceipts.employeeNotes')}>
              <TextArea
                rows={4}
                placeholder={t('general.enter', {
                  field: t('cashReceipts.employeeNotes'),
                })}
              ></TextArea>
            </Form.Item>
          </FormLayoutGroup>
        </FormSection>

        <FormActionButtons
          onCancel={() => {
            const currentValue = form.getFieldsValue();
            if (isEqual(currentValue, initialValues)) return resetAndNavigate();
            setShowConfirmation(true);
          }}
          isConfirmDisabled={
            !isFormValid ||
            isLoading ||
            fileList.some((file) => file.status === EFileStatus.UPLOADING) ||
            !isAllocationDataValid ||
            !isSettlementRateValid
          }
          confirmText={t(
            mode === 'create' ? 'cashReceipts.createReceipt' : 'general.save'
          )}
        />

        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={resetAndNavigate}
          title={t('forms.discardChangesTitle')}
          content={t('forms.discardChangesContent', {
            type: t('cashReceipts.receipt'),
          })}
          okText={t('forms.discardChangesOkText')}
          cancelText={t('forms.discardChangesCancelText')}
          type={ConfirmationModalType.DANGER}
        />
      </Form>
    </FormContainer>
  );
};
